import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";
import ModalDefault from "../modal-default";
import FieldsForm from "../../fields/fields-form";
import InfoBar from "../../info-paragraph/info-bar";
import {LoaderSmall} from "../../loader";
import {Field, FieldsManager} from "../../../../data/services/fields";
import Resources from "../../../../data/services/resources";
import {downloadWithError} from "../../../../data/actions/download";
import LocalStorage from "../../../util/localStorage";
import {getProp} from "../../../util/util-helpers";
import {toBackDateNoTime} from "../../../util/util-dates";
import {DEFAULT_DATABASE_DATETIME_FORMAT, WITH_DEFAULT_METADATA_SELECT_SEARCH_QUERY} from "../../../util/util-consts";

const UNIT_TYPE_TRUCK = '1';

/** Every failure comes back as a 400 with the code in `message`. */
const MVR_ERROR_CODES = [
    'MVR_MISSING_ASSET',
    'MVR_SINGLE_ASSET_ONLY',
    'MVR_MISSING_DATE_RANGE',
    'MVR_INVALID_DATE_RANGE',
    'MVR_ASSET_NOT_FOUND',
    'COULD_NOT_CREATE_PDF'
];

/**
 * Exports the DOT inspection, repair and maintenance record (49 CFR 396.3(b)) for one unit as a
 * PDF, with the documents attached to those work orders merged in behind it.
 *
 * Pass TruckID or TrailerID when the caller already knows the unit and the picker is hidden;
 * omit both to let the user choose one here.
 */
export default function ModalMvrRecordExport(
    {
        show,
        onClose,
        dispatch,
        translate,
        TruckID,
        TrailerID,
        unitNumber
    }) {

    const hasFixedUnit = !!TruckID || !!TrailerID;

    const downloadState = useSelector(state => state.download);
    const isDownloading = getProp(downloadState, 'isLoading', false);

    const [fields, setFields] = useState(() => getFields(hasFixedUnit));
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorCode, setErrorCode] = useState("");

    /** Helpers
     ================================================================= */
    function getSelectedUnit(fieldsUpdate) {
        if (hasFixedUnit) {
            return {TruckID: TruckID, TrailerID: TrailerID};
        }

        const isTruck = fieldsUpdate.UnitType.value === UNIT_TYPE_TRUCK;
        const selected = isTruck ? fieldsUpdate.TruckID.value : fieldsUpdate.TrailerID.value;

        return isTruck
            ? {TruckID: selected?.value, TrailerID: undefined}
            : {TruckID: undefined, TrailerID: selected?.value};
    }

    function getUnitLabel(fieldsUpdate) {
        if (hasFixedUnit) {
            return unitNumber;
        }

        return fieldsUpdate.UnitType.value === UNIT_TYPE_TRUCK
            ? fieldsUpdate.TruckID.value?.label
            : fieldsUpdate.TrailerID.value?.label;
    }

    // The server enforces this too, so a slip returns a clean 400 rather than a broken file -
    // this is only the fast path that keeps the button honest.
    function canSubmit() {
        const unit = getSelectedUnit(fields);

        return !!(unit.TruckID || unit.TrailerID) && !!fields.StartDate.value && !!fields.EndDate.value;
    }

    /** UI Events
     ================================================================= */
    function handleInputChange(name, value) {
        let fieldsUpdate = FieldsManager.updateField(fields, name, value);

        // Swapping the toggle must clear the unit that is no longer in play, otherwise both ids
        // would go out and the request is rejected.
        if (name === 'UnitType') {
            fieldsUpdate.TruckID.value = "";
            fieldsUpdate.TrailerID.value = "";
            fieldsUpdate = applyUnitTypeVisibility(fieldsUpdate);
        }

        fieldsUpdate.EndDate.errorMessage = null;
        fieldsUpdate.EndDate.metadata.errorLabelMessage = undefined;

        setErrorCode("");
        setFields(fieldsUpdate);
    }

    function handleDownloadClick() {
        const fieldsUpdate = FieldsManager.validateFields(Object.assign({}, fields));

        const StartDate = toBackDateNoTime(fieldsUpdate.StartDate.value);
        const EndDate = toBackDateNoTime(fieldsUpdate.EndDate.value);

        // Mark the field rather than only disabling - the user needs to see which end is wrong.
        if (StartDate && EndDate && EndDate < StartDate) {
            fieldsUpdate.EndDate.errorMessage = ['true'];
            fieldsUpdate.EndDate.metadata.errorLabelMessage = translate('text.MVR_INVALID_DATE_RANGE');
            setFields(fieldsUpdate);
            return;
        }

        if (!FieldsManager.checkFieldsForErrors(fieldsUpdate)) {
            setFields(fieldsUpdate);
            return;
        }

        const unit = getSelectedUnit(fieldsUpdate);
        const mode = fieldsUpdate.MVRDescription.value;

        setErrorCode("");
        setIsSubmitted(true);

        dispatch(downloadWithError({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrders,
            query: Object.assign(
                {
                    PdfMVR: 1,
                    StartDate: StartDate,
                    EndDate: EndDate,
                    name: `IRM_Record_${getUnitLabel(fieldsUpdate) ?? ""}_${StartDate}_${EndDate}.pdf`
                },
                unit.TruckID ? {TruckID: unit.TruckID} : {TrailerID: unit.TrailerID},
                mode && mode !== 'all' ? {MVRDescription: mode} : {}
            )
        }));
    }

    function handleClose() {
        setErrorCode("");
        setIsSubmitted(false);
        onClose();
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        if (show) {
            setFields(getFields(hasFixedUnit));
            setErrorCode("");
            setIsSubmitted(false);
        }
    }, [show]);

    useEffect(() => {
        if (!isSubmitted || isDownloading) return;

        setIsSubmitted(false);

        if (getProp(downloadState, 'error', false)) {
            const message = getProp(downloadState, 'errorMessage', "");
            setErrorCode(MVR_ERROR_CODES.includes(message) ? message : 'generic');
            return;
        }

        onClose();
    }, [downloadState]);

    /** Render
     ================================================================= */
    const submitDisabled = !canSubmit() || isDownloading;

    return (
        <ModalDefault
            show={show}
            title={translate('modal_heading.MVRRecordExport')}
            widthClass="max-w-lg"

            buttonLabel={
                <span className="flex items-center gap-2">
                    {isDownloading && <LoaderSmall disableContainer addClass="w-4 h-4"/>}
                    {translate('btn.download')}
                </span>
            }
            buttonDisabled={submitDisabled}
            onButtonClick={submitDisabled ? undefined : handleDownloadClick}

            closeButtonLabel={translate('btn.cancel')}
            closeButtonDisabled={isDownloading}
            onClose={handleClose}
        >
            <div className="p-6 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                    <FieldsForm
                        fieldsState={fields}
                        onChange={handleInputChange}
                        translate={translate}
                        isLoading={isDownloading}
                        selects={{
                            TruckID: {
                                api: 'api/' + Resources.TrucksQuick,
                                query: WITH_DEFAULT_METADATA_SELECT_SEARCH_QUERY({NotSoldRetired: 1}),
                                searchMap: (item) => ({value: item.TruckID, label: item.Truck})
                            },
                            TrailerID: {
                                api: 'api/' + Resources.TrailersQuick,
                                query: WITH_DEFAULT_METADATA_SELECT_SEARCH_QUERY({NotSoldRetired: 1}),
                                searchMap: (item) => ({value: item.TrailerID, label: item.Trailer})
                            },
                            MVRDescription: {
                                all: translate('text.mvr_description_all'),
                                complaint: translate('text.mvr_description_complaint'),
                                actions: translate('text.mvr_description_actions')
                            }
                        }}
                    />
                </div>

                <InfoBar>
                    {translate('text.mvr_record_includes_open')} {translate('text.mvr_record_takes_a_while')}
                </InfoBar>

                {!!errorCode && (
                    <InfoBar type="danger">
                        {translate(errorCode === 'generic' ? 'text.mvr_record_generic_error' : 'text.' + errorCode)}
                    </InfoBar>
                )}
            </div>
        </ModalDefault>
    )
}

/** FieldsForm has no hideDialog support - 'hidden' is how a field is taken off the form. */
function applyUnitTypeVisibility(fieldsUpdate) {
    if (!fieldsUpdate.UnitType) {
        return fieldsUpdate;
    }

    const isTruck = fieldsUpdate.UnitType.value === UNIT_TYPE_TRUCK;

    fieldsUpdate.TruckID.type = isTruck ? 'select-search' : 'hidden';
    fieldsUpdate.TrailerID.type = isTruck ? 'hidden' : 'select-search';

    return fieldsUpdate;
}

function getFields(hasFixedUnit) {
    const fields = Object.assign(
        {},
        hasFixedUnit ? {} : {
            // Field coerces falsy values to "", so the toggle is keyed 1/2 rather than 0/1, and
            // FieldsForm compares button-group values strictly - they must stay strings.
            UnitType: new Field('UnitType', UNIT_TYPE_TRUCK, [''], false, 'button-group', {
                data: {1: 'Truck', 2: 'Trailer'},
                addClass: "w-full",
                addContainerClass: "col-span-full"
            }),
            TruckID: new Field('TruckID', '', [''], false, 'select-search', {
                addContainerClass: "col-span-full"
            }),
            TrailerID: new Field('TrailerID', '', [''], false, 'select-search', {
                addContainerClass: "col-span-full"
            })
        },
        {
            StartDate: new Field('StartDate', moment().startOf('month').format(DEFAULT_DATABASE_DATETIME_FORMAT), ['empty'], false, 'date', {
                addContainerClass: "col-span-6"
            }),
            EndDate: new Field('EndDate', moment().endOf('month').format(DEFAULT_DATABASE_DATETIME_FORMAT), ['empty'], false, 'date', {
                addContainerClass: "col-span-6",
                minDate: (fieldsState) => fieldsState?.StartDate?.value
            }),
            MVRDescription: new Field('MVRDescription', 'all', [''], false, 'select', {
                addContainerClass: "col-span-full"
            })
        }
    );

    return applyUnitTypeVisibility(fields);
}
